/**
 * InviteExpiryBadge
 *
 * Live countdown for an admin-generated invitation. Re-renders every second
 * showing "expira en MM:SS" or "expira en HH:MM:SS" depending on duration.
 * Color shifts from emerald → amber → red as the timer drains:
 *   > 60 s remaining → emerald
 *   30–60 s          → amber
 *   < 30 s           → red + animate-pulse
 *   ≤ 0              → "Expirada" in red, no animation
 *
 * Used in /admin/invitaciones to give the operator real-time feedback
 * about when each link will go invalid — so they can DM it before the
 * expiration window closes.
 */

import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

interface Props {
  expiresAt: string | Date;
  className?: string;
}

function formatRemaining(ms: number): { text: string; totalSeconds: number } {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  if (totalSeconds <= 0) return { text: "Expirada", totalSeconds: 0 };
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) {
    return {
      text: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`,
      totalSeconds,
    };
  }
  return {
    text: `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`,
    totalSeconds,
  };
}

export default function InviteExpiryBadge({ expiresAt, className = "" }: Props) {
  const expiresAtMs =
    typeof expiresAt === "string" ? new Date(expiresAt).getTime() : expiresAt.getTime();

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!Number.isFinite(expiresAtMs)) return;
    // Tick once per second. Stop ticking when expired (no point updating).
    const tick = () => setNow(Date.now());
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAtMs]);

  if (!Number.isFinite(expiresAtMs)) return null;

  const remaining = expiresAtMs - now;
  const { text, totalSeconds } = formatRemaining(remaining);

  const expired = totalSeconds <= 0;
  const color = expired
    ? "text-red-400 border-red-500/40 bg-red-500/10"
    : totalSeconds <= 30
      ? "text-red-400 border-red-500/40 bg-red-500/10 animate-pulse"
      : totalSeconds <= 60
        ? "text-amber-300 border-amber-400/40 bg-amber-500/10"
        : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono tabular-nums ${color} ${className}`}
      aria-label={expired ? "Invitación expirada" : `Expira en ${text}`}
    >
      <Clock className="h-2.5 w-2.5" />
      {expired ? "Expirada" : `Expira en ${text}`}
    </span>
  );
}
